import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  const navigationItems = [
    { label: 'Home', anchor: 'home', href: '#home' },
    { label: 'About', anchor: 'about', href: '#about' },
    { label: 'Courses', anchor: 'courses', href: '#courses' },
    { label: 'Our Team', anchor: 'mentors', href: '#mentors' },
    { label: 'Success Stories', anchor: 'success-stories', href: '#success-stories' },
    { label: 'Contact', anchor: 'contact', href: '#contact' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    const handleSectionIntersection = () => {
      const sections = navigationItems.map(item => item.anchor);
      const currentSection = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      
      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('scroll', handleSectionIntersection);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleSectionIntersection);
    };
  }, []);

  const scrollToSection = (anchor) => {
    const element = document.getElementById(anchor);
    if (element) {
      const headerHeight = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
  };

  const handleApplyNow = () => {
    const element = document.getElementById('apply-section');
    if (element) {
      const headerHeight = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-[1100] transition-all duration-500 ease-in-out ${
          isScrolled 
            ? 'bg-white/30 backdrop-blur-xl shadow-lg rounded-full w-full mx-0'
            : 'bg-white/20 backdrop-blur-md shadow-md rounded-full max-w-6xl mx-auto mt-4'
        }`}
      >
        <div className="px-6 sm:px-8 lg:px-10">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex-shrink-0">
              <button
                onClick={() => scrollToSection('home')}
                className="flex items-center space-x-3 group"
              >
                <img 
                  src="assets/images/logo.png"
                  alt="Nipix Technology Logo" 
                  className="w-60 h-auto object-contain" 
                  style={{ maxHeight: 60 }}
                />
              </button>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navigationItems.map((item) => (
                <button
                  key={item.anchor}
                  onClick={() => scrollToSection(item.anchor)}
                  className={`text-sm font-medium transition-colors duration-250 hover:text-primary relative ${
                    activeSection === item.anchor 
                      ? 'text-primary' :'text-text-black'
                  }`}
                >
                  {item.label}
                  {activeSection === item.anchor && (
                    <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full" />
                  )}
                </button>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => scrollToSection('courses')}
                className="text-primary border-primary hover:bg-primary hover:text-white"
              >
                Start Learning
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleApplyNow}
                className="btn-primary"
              >
                Apply for Internship
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg text-text-secondary hover:text-primary hover:bg-muted transition-colors duration-250"
            >
              <Icon 
                name={isMobileMenuOpen ? "X" : "Menu"} 
                size={24} 
                strokeWidth={2}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[1200] lg:hidden">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="absolute top-0 right-0 w-80 max-w-[85vw] h-full bg-white shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 from-primary to-secondary rounded-lg flex items-center justify-center">
                  <img 
                  src="assets/images/only logo.png"
                  alt="Nipix Technology Logo" 
                  className="w-60 h-auto object-contain" 
                  style={{ maxHeight: 60 }}
                />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-text-primary">
                    Nipix Technology
                  </h2>
                  <p className="text-xs text-text-secondary -mt-1">
                    "The Future Technology"
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-lg text-text-secondary hover:text-primary hover:bg-muted transition-colors duration-250"
              >
                <Icon name="X" size={20} strokeWidth={2} />
              </button>
            </div>

            <nav className="p-6 space-y-4">
              {navigationItems.map((item) => (
                <button
                  key={item.anchor}
                  onClick={() => scrollToSection(item.anchor)}
                  className={`w-full text-left py-3 px-4 rounded-lg text-base font-medium transition-colors duration-250 ${
                    activeSection === item.anchor
                      ? 'text-primary bg-primary/10' :'text-text-secondary hover:text-primary hover:bg-muted'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-border bg-surface">
              <div className="space-y-3">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => scrollToSection('courses')}
                  className="text-primary border-primary hover:bg-primary hover:text-white"
                >
                  Start Learning
                </Button>
                <Button
                  variant="default"
                  fullWidth
                  onClick={handleApplyNow}
                  className="btn-primary"
                >
                  Apply for Internship
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
